import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * The half of the scene tracker the API cannot back.
 *
 * Design spec §5.3 asks for an exchange counter, a turn order, free-invoke counts on
 * scene aspects and a hostile flag on them. The implementation plan §3 checked each
 * against the API and found **nothing**: there is no exchange, initiative or turn-order
 * concept anywhere, and `SceneAspectDto` is `(Id, Description, Order)` — no counters,
 * no flag. Rather than drop the screen, the plan's recommendation is to keep these as
 * device-local state and say so in the app.
 *
 * That is what this module is, and it is deliberately the *only* place it lives, so
 * that a future `SceneState` resource plus two columns on `SceneAspect` is a single
 * swap rather than a hunt. Nothing outside this file may read the storage key or the
 * shape below.
 *
 * **Why `sessionStorage`, not `localStorage`.** These are table aids: an exchange number
 * matters for the next ninety seconds, not across sessions. Session storage is scoped
 * to one tab, which gives exactly the semantics the test gate describes — the counter
 * survives a reload and is absent in a second browser. `localStorage` would leave last
 * Tuesday's exchange 7 waiting for whoever opens the scene next.
 */

const STORAGE_KEY = 'harrowgate.sceneSession'

/** One scene's device-local state. Absent keys mean "never touched", not zero. */
export interface SceneSession {
  /** The exchange number the table is on. 1-based; the spec's header shows it. */
  exchange: number
  /** Character ids in initiative order. Empty until somebody arranges one. */
  turnOrder: number[]
  /** Index into `turnOrder` of whoever is acting. */
  actingIndex: number
  /** Scene-aspect id → free-invoke count. Aspects with none are simply absent. */
  freeInvokes: Record<number, number>
  /** Scene-aspect ids the table has marked hostile — the blood-tinted chips. */
  hostile: number[]
}

export const EMPTY_SESSION: SceneSession = {
  exchange: 1,
  turnOrder: [],
  actingIndex: 0,
  freeInvokes: {},
  hostile: [],
}

interface SessionStore {
  /** Scene id → its session. */
  scenes: Record<number, SceneSession>
  /**
   * The last scene opened on this device, so the Scene tab can land on it.
   *
   * This is device-local for the same reason the rest is: "the scene in play" is a
   * fact about *this* phone at *this* table, and the API has no concept of it.
   */
  lastSceneId: number | null

  openScene: (sceneId: number) => void
  endExchange: (sceneId: number) => void
  resetExchange: (sceneId: number) => void
  setTurnOrder: (sceneId: number, characterIds: number[]) => void
  advanceTurn: (sceneId: number) => void
  /** Hands the turn to a specific position — how a GM corrects the order mid-exchange. */
  setActing: (sceneId: number, index: number) => void
  setFreeInvokes: (sceneId: number, aspectId: number, count: number) => void
  toggleHostile: (sceneId: number, aspectId: number) => void
  clearScene: (sceneId: number) => void
}

/** Applies a change to one scene's session, creating it from the default if absent. */
function update(
  scenes: Record<number, SceneSession>,
  sceneId: number,
  change: (session: SceneSession) => SceneSession,
): Record<number, SceneSession> {
  const current = scenes[sceneId] ?? EMPTY_SESSION
  return { ...scenes, [sceneId]: change(current) }
}

export const useSceneSessionStore = create<SessionStore>()(
  persist(
    (set) => ({
      scenes: {},
      lastSceneId: null,

      openScene: (sceneId) => set({ lastSceneId: sceneId }),

      endExchange: (sceneId) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            exchange: session.exchange + 1,
            // A new exchange starts at the top of the order. Leaving the marker where
            // it was would have the counter and the ring disagree about whose turn it is.
            actingIndex: 0,
          })),
        })),

      resetExchange: (sceneId) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            exchange: 1,
            actingIndex: 0,
          })),
        })),

      setTurnOrder: (sceneId, characterIds) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            turnOrder: characterIds,
            actingIndex: 0,
          })),
        })),

      advanceTurn: (sceneId) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            actingIndex:
              session.turnOrder.length === 0
                ? 0
                : (session.actingIndex + 1) % session.turnOrder.length,
          })),
        })),

      setActing: (sceneId, index) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            actingIndex: Math.max(0, index),
          })),
        })),

      setFreeInvokes: (sceneId, aspectId, count) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => {
            const freeInvokes = { ...session.freeInvokes }
            // Zero is the absence of a counter, not a counter reading zero — the spec
            // draws "●2" only where there is something to spend.
            if (count <= 0) {
              delete freeInvokes[aspectId]
            } else {
              freeInvokes[aspectId] = count
            }
            return { ...session, freeInvokes }
          }),
        })),

      toggleHostile: (sceneId, aspectId) =>
        set((state) => ({
          scenes: update(state.scenes, sceneId, (session) => ({
            ...session,
            hostile: session.hostile.includes(aspectId)
              ? session.hostile.filter((id) => id !== aspectId)
              : [...session.hostile, aspectId],
          })),
        })),

      clearScene: (sceneId) =>
        set((state) => {
          const scenes = { ...state.scenes }
          delete scenes[sceneId]
          return { scenes }
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      // `lastSceneId` is persisted alongside the sessions; both are equally device-local.
      partialize: (state) => ({ scenes: state.scenes, lastSceneId: state.lastSceneId }),
    },
  ),
)

/** One scene's session, defaulted. Selectors keep components from touching the whole map. */
export function useSceneSession(sceneId: number): SceneSession {
  return useSceneSessionStore((state) => state.scenes[sceneId] ?? EMPTY_SESSION)
}

/** The scene this device last opened, for the Scene tab's landing. */
export function useLastSceneId(): number | null {
  return useSceneSessionStore((state) => state.lastSceneId)
}
